import { Trans } from '@lingui/macro'
import { Percent } from '@uniswap/sdk-core'
import { PROTOCOL_MAPPING } from 'constants/protocols'
import useTheme from 'hooks/useTheme'
import { darken } from 'polished'
import { ReactNode, useCallback } from 'react'
import { ArrowLeft } from 'react-feather'
import { Link as HistoryLink, NavLink, useLocation } from 'react-router-dom'
import { Box } from 'rebass'
import { useAppDispatch } from 'state/hooks'
import { resetMintState } from 'state/mint/actions'
import { resetMintState as resetMintV3State } from 'state/mint/v3/actions'
import styled, { keyframes } from 'styled-components/macro'
import { ThemedText } from 'theme'

import { RowBetween } from '../Row'
import SettingsTab from '../Settings'

const slideFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const Tabs = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  border-radius: 3rem;
  justify-content: left;
  gap: 18px;
`

const BorrowMenu = styled.div.attrs({ className: 'borrow-menu' })`
  position: absolute;
  top: 100%;
  left: 0;
  width: auto;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  background: ${({ theme }) => theme.bg1};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 16px 24px rgba(0, 0, 0, 0.04);
  z-index: 100;
  visibility: hidden;
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 200ms ease-out, transform 200ms ease-out;
  gap: 24px;
`

const BorrowMenuItem = styled.div`
  position: relative;
  display: inline-block;
  color: ${({ theme }) => theme.text3};
  cursor: pointer;
  &:hover > .borrow-menu,
  .borrow-menu:hover {
    visibility: visible;
    opacity: 1;
    animation: ${slideFadeIn} 200ms ease-out forwards;
  }
`

const activeClassName = 'ACTIVE'

const StyledNavLink = styled(NavLink).attrs({
  activeClassName,
})`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  justify-content: left;
  height: 2rem;
  border-radius: 3rem;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  color: ${({ theme }) => theme.text3};
  font-size: 16px;
  position: relative;
  &.${activeClassName} {
    border-radius: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.text1};
  }

  :hover,
  :focus {
    color: ${({ theme }) => darken(0.1, theme.text1)};
  }
`
const StyledHistoryLink = styled(HistoryLink)<{ flex: string | undefined }>`
  flex: ${({ flex }) => flex ?? 'none'};

  ${({ theme }) => theme.mediaWidth.upToMedium`
    flex: none;
    margin-right: 10px;
  `};
`
const StyledArrowLeft = styled(ArrowLeft)`
  color: ${({ theme }) => theme.text1};
`
export function SwapPoolTabs({ active }: { active: 'swap' | 'pool' | 'borrow' | 'points' | 'allpools' }) {
  const location = useLocation()
  const currentRoute = useCallback(() => {
    const pathname = location.pathname.split('/')
    if (pathname.includes('borrow') || pathname.includes('repay')) {
      return 'borrow'
    } else if (pathname.includes('pool') || pathname.includes('add') || pathname.includes('remove')) {
      return 'pool'
    } else if (pathname.includes('swap')) {
      return 'swap'
    } else if (pathname.includes('stake')) {
      return 'stake'
    } else if (pathname.includes('unstake')) {
      return 'unstake'
    }
    return 'points'
  }, [location])
  const newActive = currentRoute()
  return (
    <Tabs style={{ display: 'flex' }}>
      <StyledNavLink id={`swap-nav-link`} to={'/swap'} isActive={() => newActive === 'swap'}>
        <Trans>Swap</Trans>
      </StyledNavLink>
      <StyledNavLink id={`pool-nav-link`} to={'/pool'} isActive={() => newActive === 'pool'}>
        <Trans>Pool</Trans>
      </StyledNavLink>
      <BorrowMenuItem id={`borrow-nav-link`}>
        <Trans>Borrow</Trans>
        <BorrowMenu>
          {Object.values(PROTOCOL_MAPPING).map((protocol) => (
            <StyledNavLink key={`${protocol.id}-nav-link`} id={`${protocol.id}-nav-link`} to={`/borrow/${protocol.id}`}>
              <Trans>{protocol.name}</Trans>
            </StyledNavLink>
          ))}
        </BorrowMenu>
      </BorrowMenuItem>
      <StyledNavLink id={`points-nav-link`} to={'/stake'} isActive={() => newActive === 'stake'}>
        <Trans>Stake</Trans>
      </StyledNavLink>
      <StyledNavLink id={`points-nav-link`} to={'/unstake'} isActive={() => newActive === 'unstake'}>
        <Trans>Unstake</Trans>
      </StyledNavLink>
    </Tabs>
  )
}
export function AddRemoveTabs({
  adding,
  creating,
  defaultSlippage,
  positionID,
  children,
}: {
  adding: boolean
  creating: boolean
  defaultSlippage: Percent
  positionID?: string | undefined
  showBackLink?: boolean
  children?: ReactNode | undefined
}) {
  const theme = useTheme()
  // reset states on back
  const dispatch = useAppDispatch()
  const location = useLocation()

  // detect if back should redirect to v3 or v2 pool page
  const poolLink = location.pathname.includes('add/v2')
    ? '/pool/v2'
    : '/pool' + (!!positionID ? `/${positionID.toString()}` : '')

  return (
    <Tabs>
      <RowBetween style={{ padding: '1rem 1rem 0 1rem' }}>
        <StyledHistoryLink
          to={poolLink}
          onClick={() => {
            if (adding) {
              // not 100% sure both of these are needed
              dispatch(resetMintState())
              dispatch(resetMintV3State())
            }
          }}
          flex={children ? '1' : undefined}
        >
          <StyledArrowLeft stroke={theme.text2} />
        </StyledHistoryLink>
        <ThemedText.MediumHeader
          fontWeight={500}
          fontSize={20}
          style={{ flex: '1', margin: 'auto', textAlign: children ? 'start' : 'center' }}
        >
          {creating ? (
            <Trans>Create a pair</Trans>
          ) : adding ? (
            <Trans>Add Liquidity</Trans>
          ) : (
            <Trans>Remove Liquidity</Trans>
          )}
        </ThemedText.MediumHeader>
        <Box style={{ marginRight: '.5rem' }}>{children}</Box>
        <SettingsTab placeholderSlippage={defaultSlippage} />
      </RowBetween>
    </Tabs>
  )
}
